<template>
  <div class="dashboard-shell">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-container fluid class="pa-5">
      <!-- header + filters -->
      <v-layout wrap justify-space-between align-center>
        <v-flex xs12 md5>
          <span class="heading">Snake Bite Insights</span>
          <div class="subtext pt-1">
            State wide snakebite alerts and how quickly each district responded
          </div>
        </v-flex>
        <v-flex xs12 md7 pt-3 pt-md-0>
          <v-layout wrap justify-md-end>
            <v-flex xs12 sm4 md3 pr-sm-2 pb-2>
              <v-menu
                v-model="menuFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="fromDate"
                    placeholder="From Date"
                    clearable
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    hide-details
                    background-color="white"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="fromDate" @input="menuFrom = false"></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 sm4 md3 pr-sm-2 pb-2>
              <v-menu
                v-model="menuTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="toDate"
                    placeholder="To Date"
                    clearable
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    hide-details
                    background-color="white"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="toDate" @input="menuTo = false"></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 sm4 md3 pb-2>
              <v-select
                v-model="district"
                :items="districtOptions"
                placeholder="District"
                outlined
                dense
                hide-details
                :disabled="isOfficerLogin"
                background-color="white"
              ></v-select>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <!-- headline metrics -->
      <v-layout wrap justify-start pt-4>
        <v-flex xs12 sm6 md4 lg3 pa-3 v-for="(card, index) in cards" :key="index">
          <StatCard :card="card" />
        </v-flex>
      </v-layout>

      <!-- trend + network -->
      <v-layout wrap justify-start pt-3>
        <v-flex xs12 lg8 pa-2>
          <v-card elevation="0" class="panel pa-5">
            <v-layout wrap justify-space-between align-center pb-3>
              <v-flex xs8>
                <div class="cardtitle">Alerts &amp; Average Response Time</div>
                <div class="subtext">Month wise, for {{ trendYear }}</div>
              </v-flex>
              <v-flex xs4 class="text-right">
                <v-select
                  v-model="trendYear"
                  :items="yearOptions"
                  dense
                  outlined
                  hide-details
                  style="max-width: 120px; display: inline-block"
                ></v-select>
              </v-flex>
            </v-layout>
            <apexchart
              v-if="trendReady"
              type="line"
              height="300"
              :options="trendOptions"
              :series="trendSeries"
            ></apexchart>
            <div v-else class="nodata pt-4">No trend data available.</div>
          </v-card>
        </v-flex>

        <v-flex xs12 lg4 pa-2>
          <v-card elevation="0" class="panel pa-5">
            <div class="cardtitle pb-3">Alert Network</div>
            <div class="network-row" v-for="(row, index) in networkRows" :key="index">
              <div class="d-flex justify-space-between align-center">
                <div class="network-label">
                  <v-icon small class="mr-2" color="#427D2D">{{ row.icon }}</v-icon>
                  {{ row.label }}
                </div>
                <div class="network-value">{{ row.value }}</div>
              </div>
            </div>
            <div class="subtext pt-3">
              Every reported incident is pushed in real time to the District Medical Officer
              (first responder) and the Revenue / District Disaster Management Officer of that
              district.
            </div>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- district table -->
      <v-layout wrap justify-start pt-3>
        <v-flex xs12 pa-2>
          <v-card elevation="0" class="panel pa-5">
            <v-layout wrap justify-space-between align-center pb-3>
              <v-flex xs8>
                <div class="cardtitle">District Wise Response</div>
                <div class="subtext">
                  Where alerts are landing and how long the first action takes
                </div>
              </v-flex>
              <v-flex xs4 class="text-right">
                <v-btn small outlined color="#427D2D" @click="getStats()">
                  <v-icon small left>mdi-refresh</v-icon>
                  <span class="btntext">Refresh</span>
                </v-btn>
              </v-flex>
            </v-layout>

            <div style="overflow-x: auto" v-if="districtWise && districtWise.length > 0">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left tablehead">District</th>
                      <th class="text-left tablehead">Total Alerts</th>
                      <th class="text-left tablehead">Acknowledged</th>
                      <th class="text-left tablehead">Acted On</th>
                      <th class="text-left tablehead">Pending</th>
                      <th class="text-left tablehead">Closed</th>
                      <th class="text-left tablehead">Avg Response Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in districtWise" :key="index">
                      <td class="tableitems">{{ row.district }}</td>
                      <td class="tableitems">{{ row.totalAlerts }}</td>
                      <td class="tableitems">{{ row.acknowledged }}</td>
                      <td class="tableitems">{{ row.actedOn }}</td>
                      <td class="tableitems">
                        <span :class="row.pending > 0 ? 'pending-text' : ''">{{
                          row.pending
                        }}</span>
                      </td>
                      <td class="tableitems">{{ row.closed }}</td>
                      <td class="tableitems">{{ row.averageResponseTime }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
            <div v-else class="nodata pt-2">No alerts in the selected period.</div>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- officer performance -->
      <v-layout wrap justify-start pt-3 pb-6>
        <v-flex xs12 pa-2>
          <v-card elevation="0" class="panel pa-5">
            <div class="cardtitle pb-1">Officer Response</div>
            <div class="subtext pb-3">
              Alerts received against actions actually recorded, per District
              Medical Officer
            </div>

            <div style="overflow-x: auto" v-if="officers && officers.length > 0">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left tablehead">District Medical Officer</th>
                      <th class="text-left tablehead">District</th>
                      <th class="text-left tablehead">Alerts</th>
                      <th class="text-left tablehead">Opened</th>
                      <th class="text-left tablehead">Acted On</th>
                      <th class="text-left tablehead">Response Rate</th>
                      <th class="text-left tablehead">Avg Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in officers" :key="index">
                      <td class="tableitems">{{ row.name || "-" }}</td>
                      <td class="tableitems">{{ row.district || "-" }}</td>
                      <td class="tableitems">{{ row.alertsReceived }}</td>
                      <td class="tableitems">{{ row.alertsViewed }}</td>
                      <td class="tableitems">{{ row.alertsActedOn }}</td>
                      <td class="tableitems">{{ row.responseRate }}%</td>
                      <td class="tableitems">{{ row.averageResponseTime }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-layout wrap justify-center pt-3 v-if="officerPages > 1">
                <v-flex xs10>
                  <v-pagination
                    v-model="officerPage"
                    :length="officerPages"
                    :total-visible="7"
                    circle
                    color="#427D2D"
                    small
                  ></v-pagination>
                </v-flex>
              </v-layout>
            </div>
            <div v-else class="nodata pt-2">No officer activity in the selected period.</div>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import StatCard from "@/components/Common/statCard";
export default {
  components: {
    StatCard,
  },
  data() {
    var currentYear = new Date().getFullYear();
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,

      fromDate: null,
      toDate: null,
      menuFrom: false,
      menuTo: false,
      district: "All",
      districtOptions: ["All"],

      summary: {},
      network: {},
      districtWise: [],

      trendYear: currentYear,
      yearOptions: Array.from({ length: 5 }, function (_, i) {
        return currentYear - 4 + i;
      }),
      trend: [],

      officers: [],
      officerPage: 1,
      officerPages: 0,
      officerCount: 10,
    };
  },
  computed: {
    cards() {
      var s = this.summary || {};
      return [
        {
          title: "Total Snakebite Alerts",
          value: s.totalAlerts !== undefined ? s.totalAlerts : 0,
          hint: (s.alertsDistributed || 0) + " distributed to officers",
          icon: "mdi-bell-alert-outline",
          color: "#c62828",
          tint: "#fdecea",
        },
        {
          title: "Average Response Time",
          value: s.averageResponseTime || "-",
          hint: "Alert sent to first recorded action",
          icon: "mdi-timer-outline",
          color: "#1565c0",
          tint: "#e8f1fd",
        },
        {
          title: "Average Acknowledgement",
          value: s.averageAcknowledgeTime || "-",
          hint: "Alert sent to an officer opening it",
          icon: "mdi-eye-check-outline",
          color: "#6a1b9a",
          tint: "#f3e8fa",
        },
        {
          title: "Responded",
          value: (s.actedOn || 0) + " (" + (s.responseRate || 0) + "%)",
          hint: (s.acknowledged || 0) + " acknowledged",
          icon: "mdi-account-check-outline",
          color: "#2e7d32",
          tint: "#e8f5e9",
        },
        {
          title: "Awaiting Response",
          value: s.pending !== undefined ? s.pending : 0,
          hint: "No action recorded yet",
          icon: "mdi-alert-circle-outline",
          color: "#b26a00",
          tint: "#fff4e0",
        },
        {
          title: "Closed Incidents",
          value: s.closed !== undefined ? s.closed : 0,
          hint: "Response completed",
          icon: "mdi-check-decagram-outline",
          color: "#00695c",
          tint: "#e0f2f1",
        },
        {
          title: "Actions Recorded",
          value: s.totalActionsRecorded !== undefined ? s.totalActionsRecorded : 0,
          hint: "Across the state activity log",
          icon: "mdi-clipboard-text-clock-outline",
          color: "#427d2d",
          tint: "#eef6ef",
        },
        // {
        //   title: "Alerts Opened",
        //   value: (s.alertsViewed || 0) + " / " + (s.alertsDelivered || 0),
        //   hint: "Delivered alerts opened by officers",
        //   icon: "mdi-email-open-outline",
        //   color: "#455a64",
        //   tint: "#eceff1",
        // },
      ];
    },
    networkRows() {
      var n = this.network || {};
      return [
        { label: "District Medical Officers", value: n.medicalOfficers || 0, icon: "mdi-doctor" },
        { label: "Listed Hospitals", value: n.listedHospitals || 0, icon: "mdi-hospital-building" },
        { label: "Victim Records", value: n.victimRecords || 0, icon: "mdi-account-injury" },
      ];
    },
    trendReady() {
      return this.trend && this.trend.length > 0;
    },
    trendSeries() {
      return [
        {
          name: "Alerts Reported",
          type: "column",
          data: this.trend.map(function (t) {
            return t.totalAlerts;
          }),
        },
        {
          name: "Avg Response (min)",
          type: "line",
          data: this.trend.map(function (t) {
            return t.averageResponseTimeInMinutes === null ||
              t.averageResponseTimeInMinutes === undefined
              ? 0
              : Math.round(t.averageResponseTimeInMinutes);
          }),
        },
      ];
    },
    trendOptions() {
      return {
        chart: { toolbar: { show: false }, stacked: false },
        colors: ["#427D2D", "#1565c0"],
        stroke: { width: [0, 3], curve: "smooth" },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.trend.map(function (t) {
            return t.month;
          }),
        },
        yaxis: [
          {
            title: { text: "Alerts" },
            labels: {
              formatter: function (v) {
                return Math.round(v);
              },
            },
          },
          {
            opposite: true,
            title: { text: "Minutes" },
            labels: {
              formatter: function (v) {
                return Math.round(v);
              },
            },
          },
        ],
        legend: { position: "top", horizontalAlign: "right" },
        grid: { borderColor: "#eef3ee" },
      };
    },
    isOfficerLogin() {
      var storeRole = (this.$store.state && this.$store.state.userRole ? this.$store.state.userRole : "");
      var storeType = (this.$store.state && this.$store.state.userType ? this.$store.state.userType : "");
      var localRole = localStorage.getItem("role") || "";
      var localType = localStorage.getItem("type") || "";
      var localUserRole = localStorage.getItem("userRole") || "";
      var routePath = (this.$route.path || "");

      var combined = `${storeRole} ${storeType} ${localRole} ${localType} ${localUserRole} ${routePath}`.toLowerCase();

      return (
        combined.includes("district medical officer") ||
        combined.includes("districtmedicalofficer") ||
        combined.includes("dmo") ||
        combined.includes("districtmedical") ||
        combined.includes("district emergency officer") ||
        combined.includes("disaster management officer") ||
        combined.includes("disastermanagementofficer") ||
        combined.includes("disaster") ||
        combined.includes("medicalofficer") ||
        combined.includes("medical officer") ||
        combined.includes("medicalofficers") ||
        combined.includes("medical")
      );
    },
  },
  watch: {
    fromDate() {
      this.getStats();
      this.getOfficers();
    },
    toDate() {
      this.getStats();
      this.getOfficers();
    },
    district(newVal) {
      if (this.isOfficerLogin && newVal && newVal !== "All") {
        this.districtOptions = [newVal];
      }
      this.getStats();
      this.getTrend();
      this.getOfficers();
    },
    trendYear() {
      this.getTrend();
    },
    officerPage() {
      this.getOfficers();
    },
  },
  beforeMount() {
    var storedDist = localStorage.getItem("district") || (this.$store.state && this.$store.state.district);
    if (this.isOfficerLogin && storedDist && storedDist !== "All") {
      this.district = storedDist;
      this.districtOptions = [storedDist];
    }
    this.getDistricts();
    this.getStats();
    this.getTrend();
    this.getOfficers();
  },
  methods: {
    getDistricts() {
      axios({
        method: "GET",
        url: "/state/districts",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status && response.data.data) {
            if (this.isOfficerLogin && this.district && this.district !== "All") {
              this.districtOptions = [this.district];
            } else {
              this.districtOptions = ["All"].concat(response.data.data);
            }
          }
        })
        .catch(function (err) {
          console.log(err);
        });
    },

    getStats() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/state/dashboard/snakebite/stats",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          fromDate: this.fromDate,
          toDate: this.toDate,
          district: this.district,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.summary = response.data.summary || {};
            this.network = response.data.network || {};
            this.districtWise = response.data.districtWise || [];
            if (response.data.filters && response.data.filters.district && response.data.filters.district !== "All") {
              this.district = response.data.filters.district;
            }
            if (this.isOfficerLogin && this.district && this.district !== "All") {
              this.districtOptions = [this.district];
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    getTrend() {
      axios({
        method: "GET",
        url: "/state/dashboard/snakebite/trend",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { year: this.trendYear, district: this.district },
      })
        .then((response) => {
          if (response.data.status) {
            this.trend = response.data.data || response.data.monthly || [];
          } else {
            this.trend = [];
          }
        })
        .catch(function (err) {
          console.log(err);
        });
    },

    getOfficers() {
      axios({
        method: "GET",
        url: "/state/officer/performance",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          page: this.officerPage,
          limit: this.officerCount,
          fromDate: this.fromDate,
          toDate: this.toDate,
          district: this.district,
          //this table is District Medical Officers only. The filter has to go
          //to the server: the list is paged, so filtering the rows here would
          //only ever trim the page in hand and leave the paging counts wrong.
          role: "MedicalOfficer",
        },
      })
        .then((response) => {
          if (response.data.status) {
            this.officers = response.data.data || [];
            this.officerPages = response.data.pages || 0;
          } else {
            this.officers = [];
          }
        })
        .catch(function (err) {
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.heading {
  font-family: poppinsregular;
  font-size: 20px;
  font-weight: 900;
}
.cardtitle {
  font-family: poppinsregular;
  font-size: 15px;
  font-weight: 600;
  color: #22331f;
}
.subtext {
  font-family: poppinsregular;
  font-size: 12.5px;
  color: #6b7a66;
}
.btntext {
  font-family: poppinsregular;
  font-size: 11.5px;
}
/* ---------- page shell ---------- */
.dashboard-shell {
  background: #f6f8fb;
  min-height: 100vh;
  padding-bottom: 2rem;
}
.panel {
  background: #ffffff;
  border-radius: 12px !important;
  box-shadow: 0 2px 10px rgba(20, 40, 20, 0.05) !important;
}
.tablehead {
  font-family: poppinsregular;
  font-size: 12.5px !important;
  color: #000 !important;
  text-transform: uppercase !important;
  white-space: nowrap;
}
.tableitems {
  font-family: poppinsregular;
  font-size: 12.5px;
  white-space: nowrap;
}
.nodata {
  font-family: poppinsregular;
  font-size: 13px;
  color: #8a998a;
}
.pending-text {
  color: #b26a00;
  font-weight: 600;
}
.network-row {
  padding: 9px 0;
  border-bottom: 1px solid #f0f5f0;
}
.network-row:last-child {
  border-bottom: none;
}
.network-label {
  font-family: poppinsregular;
  font-size: 13px;
  color: #3a4a36;
}
.network-value {
  font-family: poppinsregular;
  font-size: 15px;
  font-weight: 700;
  color: #17240f;
}
</style>
